import { createReducer, on } from '@ngrx/store';
import {
  ConfirmationPageActions
} from '../../create-request/actions';
import * as AccountInfoActions from '../actions/account-info.actions';
import { CashInfo, CashType } from '../models';
import { StandingAuthConfirmationPageActions } from '../../standing-authorizations/actions';

export const cashFeatureKey = 'cashBalances';

export interface State extends CashInfo {}
export const initialCashState: State = {
  availableCash: { type: CashType.Available, amount: 0 },
  unsettledCash: { type: CashType.Unsettled, amount: 0 },
  marginAvailable: { type: CashType.Margin, amount: 0 }, //SAC-292902: now getting this from overview to match legacy (temporary fix)
  marketValue: { type: CashType.MarketValue, amount: 0 },
  rmdCurrentYear: { type: CashType.RmdCurrent, amount: 0 },
  rmdYtd: { type: CashType.RmdYtd, amount: 0 },
  rmdRemaining: { type: CashType.RmdRemaining, amount: 0 },
  trustPrincipalBalance: { type: CashType.trustPrincipalBalance, amount: 0 },
  trustIncomeBalance: { type: CashType.trustIncomeBalance, amount: 0 },
  contributionYtd: { type: CashType.contributionYtd, amount: 0 },
  // PAL balance fields
  currentBalance: {type: CashType.OutstandingLoans, amount: 12123.45},
  availableCredit: {type: CashType.AvailableCredit, amount: 234.56},
  displayCredit: {type: CashType.CreditLine, amount: 34567.89},
  billingTotalChargesDue: {type: CashType.FinanceCharges, amount: 543.21},
  billingTotalPaymentDue: {type: CashType.TotalPaymentDue, amount: 4567.89},
  billingPaymentDueDt: {type: CashType.PaymentDueDate, amount: 'N/A'},
};

export const cashReducer = createReducer(
  initialCashState,
  on(AccountInfoActions.updateSubAccount, () => {
    return {
      ...initialCashState
    }
  }),
  on(AccountInfoActions.loadAccountInfoSuccess, (state, accountInfo) => {
    const { registrationType } = accountInfo;
    if (registrationType.includes('Part QRP/Keo') || registrationType.includes('I401(k)'))
    {
      return {
        ...state,
        availableCash: {
          type: CashType.Available,
          amount: accountInfo.cashBalances.availableCash.amount,
        },
        unsettledCash: {
          type: CashType.Unsettled,
          amount: accountInfo.cashBalances.unsettledCash.amount,
        },
        marketValue: {
          type: CashType.MarketValue,
          amount: accountInfo.cashBalances.marketValue.amount,
        },
        trustPrincipalBalance: {
          type: CashType.trustPrincipalBalance,
          amount: accountInfo.cashBalances.trustPrincipalBalance.amount,
        },
        trustIncomeBalance: {
          type: CashType.trustIncomeBalance,
          amount: accountInfo.cashBalances.trustIncomeBalance.amount,
        },
        contributionYtd: {
          type: CashType.contributionYtd,
          amount: accountInfo.cashBalances.contributionYtd.amount,
        },
        marginAvailable: {
          type: CashType.Margin,
          amount: accountInfo.cashBalances.marginAvailable.amount
        }
      };
    }
    else {
      return {
        ...state,
        availableCash: {
          type: CashType.Available,
          amount: accountInfo.cashBalances.availableCash.amount,
        },
        unsettledCash: {
          type: CashType.Unsettled,
          amount: accountInfo.cashBalances.unsettledCash.amount,
        },
        marketValue: {
          type: CashType.MarketValue,
          amount: accountInfo.cashBalances.marketValue.amount,
        },
        rmdCurrentYear: {
          type: CashType.RmdCurrent,
          amount: accountInfo.cashBalances.rmdCurrentYear.amount,
        },
        rmdYtd: {
          type: CashType.RmdYtd,
          amount: accountInfo.cashBalances.rmdYtd.amount,
        },
        rmdRemaining: {
          type: CashType.RmdRemaining,
          amount: accountInfo.cashBalances.rmdRemaining.amount,
        },
        trustPrincipalBalance: {
          type: CashType.trustPrincipalBalance,
          amount: accountInfo.cashBalances.trustPrincipalBalance.amount,
        },
        trustIncomeBalance: {
          type: CashType.trustIncomeBalance,
          amount: accountInfo.cashBalances.trustIncomeBalance.amount,
        },
        contributionYtd: {
          type: CashType.contributionYtd,
          amount: accountInfo.cashBalances.contributionYtd.amount,
        },
        marginAvailable: {
          type: CashType.Margin,
          amount: accountInfo.cashBalances.marginAvailable.amount
        },
        // Map PAL balance fields if present
        currentBalance: accountInfo.cashBalances.currentBalance ? {
          type: CashType.OutstandingLoans,
          amount: accountInfo.cashBalances.currentBalance.amount,
        } : undefined,
        availableCredit: accountInfo.cashBalances.availableCredit ? {
          type: CashType.AvailableCredit,
          amount: accountInfo.cashBalances.availableCredit.amount,
        } : undefined,
        displayCredit: accountInfo.cashBalances.displayCredit ? {
          type: CashType.CreditLine,
          amount: accountInfo.cashBalances.displayCredit.amount,
        } : undefined,
        billingTotalChargesDue: accountInfo.cashBalances.billingTotalChargesDue ? {
          type: CashType.FinanceCharges,
          amount: accountInfo.cashBalances.billingTotalChargesDue.amount,
        } : undefined,
        billingTotalPaymentDue: accountInfo.cashBalances.billingTotalPaymentDue ? {
          type: CashType.TotalPaymentDue,
          amount: accountInfo.cashBalances.billingTotalPaymentDue.amount,
        } : undefined,
        billingPaymentDueDt: accountInfo.cashBalances.billingPaymentDueDt ? {
          type: CashType.PaymentDueDate,
          amount: accountInfo.cashBalances.billingPaymentDueDt.amount,
        } : undefined,
      };
    }
  }),
  on(AccountInfoActions.loadPalAccountInfoSuccess, (state, accountInfo) => {
    return {
      ...state,
      currentBalance: {
        type: CashType.OutstandingLoans,
        amount: accountInfo.accountInfo.currentBalance,
      },
      availableCredit: {
        type: CashType.AvailableCredit,
        amount: accountInfo.accountInfo.availableCredit,
      },
      displayCredit: {
        type: CashType.CreditLine,
        amount: accountInfo.accountInfo.displayCredit,
      },
      billingTotalChargesDue: {
        type: CashType.FinanceCharges,
        amount: accountInfo.amountDue.billingTotalChargesDue,
      },
      billingTotalPaymentDue: {
        type: CashType.TotalPaymentDue,
        amount: accountInfo.amountDue.billingTotalPaymentDue,
      },
      billingPaymentDueDt: {
        type: CashType.PaymentDueDate,
        amount: accountInfo.amountDue.billingPaymentDueDt || 'N/A',
      },
    };
  }),
  on(
    AccountInfoActions.loadTransactionsSuccess,
    (state, statusviewResponse) => {
      const registrationType = statusviewResponse?.accountDisplayDetails?.regShortName;
      if (registrationType?.includes('Part QRP/Keo') || registrationType?.includes('I401(k)'))
      {
        return {
          ...state,
        // Temporary fix for SAC-249158 / SAC-250407 - this should be removed when the trust indicator in /account/info is fixed
          trustPrincipalBalance: {
            type: CashType.trustPrincipalBalance,
            amount: +statusviewResponse.trustPrincipalBalance,
          },
          trustIncomeBalance: {
            type: CashType.trustIncomeBalance,
            amount: +statusviewResponse.trustIncomeBalance,
          },
          rmdCurrentYear: {
            type: CashType.RmdCurrent,
            amount: +statusviewResponse.rmdDetails.rmdAnnualAmount,
          },
          rmdYtd: {
            type: CashType.RmdYtd,
            amount: +statusviewResponse.rmdDetails.rmdAnnualPaidYearToDateAmount,
          },
          rmdRemaining: {
            type: CashType.RmdRemaining,
            amount: +statusviewResponse.rmdDetails.rmdAnnualRemainingAmount,
          }
        };
      }
      else {
        return {
          ...state,
        // Temporary fix for SAC-249158 / SAC-250407 - this should be removed when the trust indicator in /account/info is fixed
          trustPrincipalBalance: {
            type: CashType.trustPrincipalBalance,
            amount: +statusviewResponse.trustPrincipalBalance,
          },
          trustIncomeBalance: {
            type: CashType.trustIncomeBalance,
            amount: +statusviewResponse.trustIncomeBalance,
          },
        };
      }
    }
  ),
  on(
    AccountInfoActions.loadAccountInfoFailure,
    AccountInfoActions.loadAccountInfoServiceFailure,
    ConfirmationPageActions.createAnotherRequestClicked,
    ConfirmationPageActions.viewTransactionStatusClicked,
    StandingAuthConfirmationPageActions.createNewStandingAuthClicked,
    StandingAuthConfirmationPageActions.returnStandingAuthClicked,
    () => {
      return initialCashState;
    }
  )
);
